import { useEffect, useRef, useState } from 'react';
import { categoriesApi } from '@apis/api-helper';
import { getErrorString } from '@utils/utils';
import { getModalValue, getQueryParamsForCategoryModal } from './helper';
import { strings } from '@constants/strings';
import { ErrorMessage, Select, Modal, Toast } from '@common-web';
import CategoryModalBody from './modal-body';
import { makeTitleCase } from '../../../utils/utils';

const CategoryModal = ({
  placeholder = '',
  onChange = () => {},
  label = '',
  modalOpenByDefault = false,
  preselectedCategory = false,
  errorMsg = '',
  value = null,
  required = true,
  disabled = false,
}) => {
  const [modalOpen, setModalOpen] = useState(modalOpenByDefault);
  const [list, setList] = useState([]);
  const abortController = useRef();
  const [snapShot, setSnapshot] = useState({
    selected: null,
    pagination: null,
    paginationChild: null,
    paginationSubChild: null,
    list: [],
    searchQuery: '',
    childSearchQuery: '',
    subChildSearchQuery: '',
  });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState({});
  const [pagination, setPagination] = useState(null);
  const [paginationChild, setPaginationChild] = useState(null);
  const [paginationSubChild, setPaginationSubChild] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [childSearchQuery, setChildSearchQuery] = useState('');
  const [subChildSearchQuery, setSubChildSearchQuery] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      getAllCategoriesList({
        searchQuery,
        childSearchQuery,
        subChildSearchQuery,
        loading: { parent: true },
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getAllCategoriesList({
        searchQuery,
        childSearchQuery,
        subChildSearchQuery,
        loading: { child: true },
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [childSearchQuery]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getAllCategoriesList({
        searchQuery,
        childSearchQuery,
        subChildSearchQuery,
        loading: { subChild: true },
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [subChildSearchQuery]);


  const onInputChange = (event) => {
    setSearchQuery(event?.target?.value);
    setChildSearchQuery('');
    setSubChildSearchQuery('');
  };

  const onChildInputChange = (event) => {
    setChildSearchQuery(event?.target?.value);
    setSubChildSearchQuery('');
  };

  const onSubChildInputChange = (event) => {
    setSubChildSearchQuery(event?.target?.value);
  };

  const checkObjectIsValid = ({ object }) => {
    return !!object?.parent && !!object?.child;
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearchQuery('');
    setChildSearchQuery('');
    setSubChildSearchQuery('');
  };

  useEffect(() => {
    if (modalOpen) {
      if (preselectedCategory || checkObjectIsValid({ object: value })) {
        getAllCategoriesList({ loading: { parent: true, child: true, subChild: true } });
      } else {
        const params = getQueryParamsForCategoryModal(value);
        getCategoriesList(params);
      }
    }
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen && !!value) {
      getAllCategoriesList({});
    }
  }, []);

  useEffect(() => {
    if (!!value) {
      setSelected(value);
      if (preselectedCategory && !snapShot?.selected) {
        setSnapshot({
          list,
          selected: value,
          pagination,
          paginationChild,
          paginationSubChild,
          searchQuery,
          childSearchQuery,
          subChildSearchQuery,
        });
      }
    }
  }, [value]);

  async function getAllCategoriesList({
    searchQuery = '',
    childSearchQuery = '',
    subChildSearchQuery = '',
    loading = {},
  }) {
    setLoading(loading);
    try {
      const parentParams = getQueryParamsForCategoryModal(null, 1, searchQuery);
      const childParams = getQueryParamsForCategoryModal({ ...selected, child: null }, null, childSearchQuery); // when `child` is null in item, l2 api params re retrieved
      const subChildParams = getQueryParamsForCategoryModal(selected, null, subChildSearchQuery);
      const promiseArray = selected?.child?.id
        ? [categoriesApi(parentParams), categoriesApi(childParams), categoriesApi(subChildParams)]
        : [categoriesApi(parentParams), categoriesApi(childParams)];
      const values = await Promise.all(promiseArray);
      const childList = values[1]?.categories;
      const subChildList = values?.[2]?.categories;
      const updatedChildList = childList?.map((item) => {
        if (subChildList?.every((childItem) => childItem?.parent_id === item?.id)) {
          return {
            ...item,
            subChild: subChildList,
          };
        }
        return {
          ...item,
          subChild: [],
        };
      });
      const formattedList = values[0]?.categories?.map((item) => {
        if (childList?.every((childItem) => childItem?.parent_id === item?.id)) {
          return {
            ...item,
            child: updatedChildList,
          };
        }
        return {
          ...item,
        };
      });
      setList([...formattedList]);
      setPagination(values[0]?.pagination);
      setPaginationChild(values[1]?.pagination);
      selected?.child?.id && setPaginationSubChild(values?.[2]?.pagination);
    } catch (error) {
      Toast.error(getErrorString(error));
    } finally {
      setLoading({});
    }
  }

  async function getCategoriesList(params = '', selectedItem) {
    let checkFromChildSearch = selectedItem?.parent?.id;
    let checkSubChildSearch = selectedItem?.child?.id;
    if (abortController?.current) {
      abortController.current?.abort();
    }
    abortController.current = new AbortController();
    if (selectedItem?.child && selectedItem?.child?.displayed_children_count) { setLoading({ subChild: true }); }
    else if (!selectedItem) { setLoading({ parent: true }); }
    else if (selectedItem?.parent) { setLoading({ child: true }); }

    try {
      const res = await categoriesApi(params, abortController?.current?.signal);
      if (checkSubChildSearch) {
        const updatedList = list.map((item) => {
          if (item?.child) {
            item.child = item?.child?.map((child) => ({
              ...child,
              subChild: child?.id === res?.categories[0]?.parent_id ? res?.categories : [],
            }));
          }
          return item;
        });
        setList(updatedList);
        setPaginationSubChild(res?.pagination);
      } else if (checkFromChildSearch) {
        setList(
          list?.map((item) => {
            if (item?.id === res?.categories[0]?.parent_id) {
              return {
                ...item,
                child: res?.categories,
              };
            } else {
              return {
                ...item,
                child: [],
              };
            }
          })
        );
        setPaginationChild(res?.pagination);
      } else {
        setList(
          res?.categories?.map((item) => {
            return {
              ...item,
              child: [],
            };
          })
        );
        setPagination(res?.pagination);
        setPaginationChild(null);
      }
    } catch (error) {
      Toast.error(getErrorString(error));
    } finally {
      setLoading({});
    }
  }

  const handleItemClick = (item, type) => {
    let selectedItem = {};
    switch (type) {
      case 'parent':
        selectedItem = {
          parent: item,
          subChild: null,
          child: null,
        };
        break;
      case 'child':
        selectedItem = {
          parent: selected?.parent,
          child: item,
          subChild: null,
        };
        break;
      case 'sub-child':
        selectedItem = {
          parent: selected?.parent,
          child: selected?.child,
          subChild: item,
        };
        break;
    }
    setSelected(selectedItem);
    onChange(selectedItem);
    const params = getQueryParamsForCategoryModal(selectedItem, null);
    getCategoriesList(params, selectedItem);
    if (!item?.displayed_children_count) {
      setSnapshot({
        list,
        selected: selectedItem,
        pagination,
        paginationChild,
        paginationSubChild,
        searchQuery,
        childSearchQuery,
        subChildSearchQuery,
      });
      closeModal();
    }
  };

  const selectedValue = getModalValue(selected);

  return (
    <>
      <div
        onClick={() => {
          !disabled && setModalOpen(true);
        }}
      >
        <Select
          placeholder={placeholder}
          disabled={disabled}
          value={selectedValue ? { label: selectedValue } : null}
          label={label}
          required={required}
          style={{ caretColor: 'transparent' }}
          dropdownStyle={{ display: 'none' }}
        />
      </div>

      <Modal
        title={makeTitleCase({ string: strings?.select_a_category })}
        open={modalOpen}
        ModalBody={
          <CategoryModalBody
            list={list}
            setList={setList}
            selected={selected}
            loading={loading}
            pagination={pagination}
            paginationChild={paginationChild}
            setPagination={setPagination}
            setPaginationChild={setPaginationChild}
            setPaginationSubChild={setPaginationSubChild}
            paginationSubChild={paginationSubChild}
            searchQuery={searchQuery}
            childSearchQuery={childSearchQuery}
            setChildSearchQuery={setChildSearchQuery}
            onInputChange={onInputChange}
            onChildInputChange={onChildInputChange}
            onSubChildInputChange={onSubChildInputChange}
            subChildSearchQuery={subChildSearchQuery}
            handleItemClick={handleItemClick}
          />
        }
        showActions={false}
        width={866}
        modalStyles={{ '--ant-modal-body-padding': 0, '--ant-modal-body-max-height': '500px' }}
        maskClosable={false}
        closable={true}
        handleCancel={() => {
          closeModal();
          setSelected(snapShot?.selected);
          setList(snapShot?.list);
          setPagination(snapShot?.pagination);
          setPaginationChild(snapShot?.paginationChild);
          setPaginationSubChild(snapShot?.paginationSubChild);
          onChange(snapShot?.selected);
        }}
      />
      <ErrorMessage errorMsg={errorMsg} />
    </>
  );
};

export default CategoryModal;
